import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { CheckIcon, Loader2Icon, UserIcon, DatabaseIcon, ShieldIcon, SettingsIcon } from 'lucide-react';
import { StepProps } from '../../types/onboarding';
interface ReviewCreateProps extends StepProps {
  onBack: () => void;
}
export function ReviewCreate({
  data,
  setCanAdvance,
  onBack
}: ReviewCreateProps) {
  const [isCreating, setIsCreating] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  useEffect(() => {
    setCanAdvance(true);
  }, [setCanAdvance]);
  const handleCreate = () => {
    setIsCreating(true);
    setTimeout(() => {
      setIsCreating(false);
      setIsSuccess(true);
    }, 1500);
  };
  const isLoanOfficer = data.profile.includes('Loan Officer');
  const isProcessor = data.role.includes('Processor');
  const hasEinstein = data.permissionSets.includes('Loan Officer Permission Set') || data.permissionSets.includes('Processor Permission Set');
  const DataRow = ({
    label,
    value
  }: {
    label: string;
    value: any;
  }) => <div className="py-2 flex justify-between border-b border-gray-100 last:border-0" data-id="element-218">
      <span className="text-sm text-gray-500" data-id="element-219">{label}</span>
      <span className="text-sm font-medium text-gray-900 text-right max-w-[60%] truncate" data-id="element-220">
        {value || <span className="text-gray-400 italic" data-id="element-221">None</span>}
      </span>
    </div>;
  if (isSuccess) {
    return <div className="py-12 text-center flex flex-col items-center justify-center min-h-[400px]" data-id="element-222">
        <motion.div initial={{
        scale: 0
      }} animate={{
        scale: 1
      }} transition={{
        type: 'spring',
        stiffness: 200,
        damping: 20
      }} className="w-20 h-20 bg-[#2E844A] rounded-full flex items-center justify-center shadow-lg mb-6" data-id="element-223">
          <CheckIcon className="w-10 h-10 text-white" data-id="element-224" />
        </motion.div>
        <motion.h2 initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} className="text-2xl font-bold text-gray-900 mb-2" data-id="element-225">
          User Successfully Created!
        </motion.h2>
        <motion.p initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} className="text-sm text-gray-600 mb-10" data-id="element-226">
          {data.firstName} {data.lastName} ({data.username}) has been fully
          onboarded.
        </motion.p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10 text-left w-full max-w-2xl" data-id="element-227">
          {[{
          icon: UserIcon,
          count: 1,
          label: 'User'
        }, {
          icon: DatabaseIcon,
          count: 1,
          label: 'Account'
        }, {
          icon: ShieldIcon,
          count: data.permissionSets.length,
          label: 'Perm Sets'
        }, {
          icon: SettingsIcon,
          count: isLoanOfficer ? 4 : 3,
          label: 'Metadata'
        }].map((item, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2 + i * 0.1
        }} className="bg-white border border-gray-100 rounded-xl p-4 flex items-center space-x-4 shadow-sm" data-id="element-228">
              <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0" data-id="element-229">
                <item.icon className="w-5 h-5 text-[#2563EB]" data-id="element-230" />
              </div>
              <div data-id="element-231">
                <div className="text-xl font-bold text-gray-900 leading-none" data-id="element-232">
                  {item.count}
                </div>
                <div className="text-[10px] font-medium text-gray-500 uppercase tracking-wider mt-1" data-id="element-233">
                  {item.label}
                </div>
              </div>
            </motion.div>)}
        </div>

        <motion.button initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.8
      }} onClick={() => window.location.reload()} className="px-6 py-2.5 bg-white border border-gray-300 text-sm text-gray-700 font-medium rounded-full shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#2563EB] transition-colors" data-id="element-234">
          Start New Onboarding
        </motion.button>
      </div>;
  }
  return <div className="flex flex-col h-full" data-id="element-235">
      <div className="flex-1 space-y-8" data-id="element-236">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8" data-id="element-237">
          <div data-id="element-238">
            <h4 className="text-xs font-medium text-gray-400 uppercase tracking-wider border-b border-gray-100 pb-2 mb-3" data-id="element-239">
              User & Account
            </h4>
            <DataRow label="Name" value={`${data.firstName} ${data.lastName}`} data-id="element-240" />
            <DataRow label="Username" value={data.username} data-id="element-241" />
            <DataRow label="Profile" value={data.profile} data-id="element-242" />
            <DataRow label="Role" value={data.role} data-id="element-243" />
          </div>
          <div data-id="element-244">
            <h4 className="text-xs font-medium text-gray-400 uppercase tracking-wider border-b border-gray-100 pb-2 mb-3" data-id="element-245">
              Operational
            </h4>
            <DataRow label="Team" value={data.team} data-id="element-246" />
            <DataRow label="Call Center" value={data.callCenter} data-id="element-247" />
            <DataRow label="States" value={data.statesLicensed.length ? `${data.statesLicensed.length} selected` : 'None'} data-id="element-248" />
            <DataRow label="Groups" value={data.publicGroups.length ? `${data.publicGroups.length} selected` : 'None'} data-id="element-249" />
          </div>
          <div data-id="element-250">
            <h4 className="text-xs font-medium text-gray-400 uppercase tracking-wider border-b border-gray-100 pb-2 mb-3" data-id="element-251">
              Access
            </h4>
            <DataRow label="Packages" value={data.packageLicenses.length ? `${data.packageLicenses.length} selected` : 'None'} data-id="element-252" />
            <DataRow label="Perm Sets" value={data.permissionSets.length ? `${data.permissionSets.length} selected` : 'None'} data-id="element-253" />
          </div>
          <div data-id="element-254">
            <h4 className="text-xs font-medium text-gray-400 uppercase tracking-wider border-b border-gray-100 pb-2 mb-3" data-id="element-255">
              Automated Metadata
            </h4>
            <ul className="text-sm text-gray-700 space-y-1.5 list-disc list-inside py-2" data-id="element-256">
              <li data-id="element-257">Round Robin Rules</li>
              <li data-id="element-258">Shift Definitions</li>
              <li data-id="element-259">Commissions ({isProcessor ? 'Processor' : 'Standard'})</li>
              {isLoanOfficer && <li data-id="element-260">Weighting Rules</li>}
              {hasEinstein && <li data-id="element-261">Einstein Activity Capture</li>}
            </ul>
          </div>
        </div>
      </div>

      {/* Inline Navigation for Review Step */}
      <div className="mt-12 pt-6 border-t border-gray-100 flex items-center justify-between" data-id="element-262">
        <button onClick={onBack} className="text-sm font-medium text-gray-500 hover:text-gray-900 hover:underline transition-colors" data-id="element-263">
          Previous
        </button>

        <button onClick={handleCreate} disabled={isCreating} className="flex items-center justify-center px-8 py-2.5 bg-[#2563EB] text-white text-sm font-medium rounded-full shadow-sm hover:bg-[#1D4ED8] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#2563EB] disabled:opacity-70 disabled:cursor-not-allowed transition-all" data-id="element-264">
          {isCreating ? <>
              <Loader2Icon className="w-4 h-4 mr-2 animate-spin" data-id="element-265" />
              Creating User...
            </> : 'Create User'}
        </button>
      </div>
    </div>;
}